import {
  CONVERSATION_PRIORITY_ORDER,
  MESSAGE_STATUS,
  MESSAGE_TYPE,
} from 'shared/constants/messages';

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

export const filterByStatus = (chatStatus, filterStatus) =>
  filterStatus === 'all' ? true : chatStatus === filterStatus;

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  // BonBoZ: che do gop trang gui mot mang inbox, cac man khac van gui mot id.
  // Khong nhan mang thi Number([2,3]) ra NaN va danh sach rong sach.
  const ids = Array.isArray(inboxId) ? inboxId : [inboxId];
  if (!ids.length) return shouldFilter;
  const isOnInbox = ids.some(id => Number(id) === chatInboxId);
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = Number(teamId) === chatTeamId;
  return teamId ? isOnTeam && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};
export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  const { inboxId, status, labels = [], teamId, conversationType } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
  } = conversation;
  const team = meta.team || {};
  const { id: chatTeamId } = team;

  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );

  return shouldFilter;
};

/**
 * Filters conversations based on user role and permissions
 *
 * @param {Object} conversation - The conversation object to check permissions for
 * @param {string} role - The user's role (administrator, agent, etc.)
 * @param {Array<string>} permissions - List of permission strings the user has
 * @param {number|string} currentUserId - The ID of the current user
 * @returns {boolean} - Whether the user has permissions to access this conversation
 */
export const applyRoleFilter = (
  conversation,
  role,
  permissions,
  currentUserId
) => {
  // the role === "agent" check is typically not correct on it's own
  // the backend handles this by checking the custom_role_id at the user model
  // here however, the `getUserRole` returns "custom_role" if the id is present,
  // so we can check the role === "agent" directly
  if (['administrator', 'agent'].includes(role)) {
    return true;
  }

  // Check for full conversation management permission
  if (permissions.includes('conversation_manage')) {
    return true;
  }

  const conversationAssignee = conversation.meta.assignee;
  const isUnassigned = !conversationAssignee;
  const isAssignedToUser = conversationAssignee?.id === currentUserId;

  // Check unassigned management permission
  if (permissions.includes('conversation_unassigned_manage')) {
    return isUnassigned || isAssignedToUser;
  }

  // Check participating conversation management permission
  if (permissions.includes('conversation_participating_manage')) {
    return isAssignedToUser;
  }

  return false;
};

// BonBoZ: kieu sap xep rieng cua ban nay - chua xem len dau. Gia tri giu nguyen
// 'unread_first' vi day la tham so `sort_by` ma API dang nhan.
// BonBoZ: mirrors Message.customer_visible on the server. Private notes,
// activity lines and failed sends are not seen by the customer.
const CUSTOMER_VISIBLE_TYPES = [
  MESSAGE_TYPE.INCOMING,
  MESSAGE_TYPE.OUTGOING,
  MESSAGE_TYPE.TEMPLATE,
];
export const isCustomerVisible = message =>
  CUSTOMER_VISIBLE_TYPES.includes(message?.message_type) &&
  !message.private &&
  message.status !== MESSAGE_STATUS.FAILED;

export const SORT_UNSEEN_FIRST = 'unread_first';

// BonBoZ: "chua xem" nghia la khach dang cho minh tra loi. Server tra ve san
// trong cot `unseen_since` (0 = da xem xong), nen client khong con phai tu suy
// ra tu unread_count va tin nhan cuoi nua - do la cho hai ben tung noi khac
// nhau: the co cham do ma badge khong dem, hoac nguoc lai.
export const isUnseen = chat => Boolean(chat?.unseen_since);

// Tin den qua ActionCable chi cap nhat `timestamp`, con `last_activity_at` phai
// doi lan fetch sau. Lay cai lon hon de hoi thoai vua co tin len dau ngay.
export const lastActivityAt = chat =>
  Math.max(chat?.last_activity_at || 0, chat?.timestamp || 0);

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
  priority_desc_created_at_asc: ['sortOnPriorityCreatedAt', 'desc'],
  // BonBoZ: backend da co scope `sort_on_unread` nhung phia client thi khong,
  // nen getter sap lai list va nuot mat thu tu server tra ve.
  unread: ['sortOnUnread', 'desc'],
  [SORT_UNSEEN_FIRST]: ['sortOnUnseenFirst', 'desc'],
};
const sortAscending = (valueA, valueB) => valueA - valueB;
const sortDescending = (valueA, valueB) => valueB - valueA;

const getSortOrderFunction = sortOrder =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.last_activity_at, b.last_activity_at),

  sortOnCreatedAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.created_at, b.created_at),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;

    return getSortOrderFunction(sortDirection)(p1, p2);
  },

  sortOnPriorityCreatedAt: (a, b) => {
    const DEFAULT_FOR_NULL = 0;
    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;
    if (p1 !== p2) return p2 - p1;
    return a.created_at - b.created_at;
  },

  // Khop voi scope `sort_on_unread` cua Rails: chua doc len dau, con lai giu
  // thu tu hoat dong gan nhat. Doc xong thi dem ve 0 va hang tu roi lai cho cu.
  sortOnUnread: (a, b) => {
    const chuaDocA = a.unread_count || 0;
    const chuaDocB = b.unread_count || 0;
    if (chuaDocA !== chuaDocB) return chuaDocB - chuaDocA;
    return b.last_activity_at - a.last_activity_at;
  },

  // Chua xem len dau; trong moi nhom, hoi thoai co tin nhan gan nhat len tren,
  // bat ke khach hay nhan vien nhan. Cung bieu thuc voi
  // `ConversationFinder::UNSEEN_FIRST_ORDER` o server.
  sortOnUnseenFirst: (a, b) =>
    isUnseen(b) - isUnseen(a) || lastActivityAt(b) - lastActivityAt(a),

  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    if (!a.waiting_since || !b.waiting_since) {
      if (!a.waiting_since && !b.waiting_since) {
        return sortFunc(a.created_at, b.created_at);
      }
      return sortFunc(a.waiting_since ? 0 : 1, b.waiting_since ? 0 : 1);
    }

    return sortFunc(a.waiting_since, b.waiting_since);
  },
};

export const sortComparator = (a, b, sortKey) => {
  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;
  return sortConfig[sortMethod](a, b, sortDirection);
};

// BonBoZ: quyet dinh gia tri inbox_id gui len API. Trong mot inbox cu the thi
// theo route; con lai thi theo trang da chon (rong = tat ca, khong gui gi).
export const resolveInboxFilter = (routeInboxId, selectedInboxIds = []) => {
  if (routeInboxId) return routeInboxId;
  return selectedInboxIds.length ? selectedInboxIds : undefined;
};
